/**
 * TSFGeocoder.ts - TSF-03
 * 
 * Clase principal del sistema TSF (Two-Stage Filtering).
 * Geocodificación en dos fases usando datos locales DERA.
 * 
 * ESTRATEGIAS:
 * - SINGLETON: Único candidato en municipio → resolución directa (98% conf)
 * - FUZZY: Múltiples candidatos → búsqueda por similitud de nombre
 * 
 * RENDIMIENTO VALIDADO (TSF-01):
 * - SINGLETON: 40% resolución directa
 * - FUZZY: 7.5% resolución adicional
 * - Total: 47.5% éxito sin llamadas a APIs externas
 * 
 * @version 1.0.0
 * @date 2025-01-08
 */

import { FastFuzzy, calculateSimilarity } from '../fuzzySearch';
import { elegirCentroEducativo, esSoloPrefijoEducativo, elegirUnicoDelTipo } from '../siglasEducativas';
import { 
  nombreConTipoDera,
  getFeaturesByMunicipio, 
  loadLocalData, 
  isDataLoaded,
  TYPOLOGY_TO_CATEGORY,
  type LocalFeature,
  type InfrastructureCategory 
} from '../LocalDataService';
import type { InfrastructureType } from '../InfrastructureClassifier';
import {
  type TSFConfig,
  type TSFResult,
  type TSFGeocodingRequest,
  type TSFGeocodingResponse,
  type TSFResolutionStatus as _TSFResolutionStatus,
  DEFAULT_TSF_CONFIG,
  calculateConfidence,
} from './types';

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Mapea InfrastructureType a InfrastructureCategory[]
 */
function typeToCategories(tipo: InfrastructureType): InfrastructureCategory[] {
  const mapping = TYPOLOGY_TO_CATEGORY[tipo];
  if (mapping === null || mapping === undefined) return [];
  if (Array.isArray(mapping)) return mapping;
  return [mapping];
}

// ============================================================================
// CLASE PRINCIPAL
// ============================================================================

/**
 * Geocodificador TSF (Two-Stage Filtering)
 * 
 * @example
 * ```typescript
 * const geocoder = new TSFGeocoder();
 * const result = await geocoder.geocode({
 *   nombre: 'Centro de Salud',
 *   tipo: InfrastructureType.HEALTH,
 *   codMunicipio: '18087'
 * });
 * 
 * if (result.resolved) {
 *   console.log(`Coordenadas: ${result.coordenadas.x}, ${result.coordenadas.y}`);
 * }
 * ```
 */
export class TSFGeocoder {
  private config: TSFConfig;
  
  constructor(config?: Partial<TSFConfig>) {
    this.config = { ...DEFAULT_TSF_CONFIG, ...config };
  }
  
  /**
   * Busca features en un municipio por tipo de infraestructura
   */
  async searchByMunicipio(
    codMun: string, 
    tipo: InfrastructureType
  ): Promise<LocalFeature[]> {
    // Asegurar datos cargados
    if (!isDataLoaded()) {
      await loadLocalData();
    }
    
    // Mapear tipo a categorías
    const categorias = typeToCategories(tipo);
    if (categorias.length === 0) {
      return [];
    }
    
    // Obtener features del municipio filtrando por categorías
    const features = await getFeaturesByMunicipio(codMun, categorias);
    return features;
  }
  
  /**
   * Estrategia SINGLETON: único candidato = resolución directa
   */
  resolveSingleton(features: LocalFeature[]): TSFResult {
    const candidateCount = features.length;
    
    if (candidateCount === 0) {
      return {
        status: 'NO_LOCAL_DATA',
        resolved: false,
        candidate: null,
        candidateCount: 0,
        confidence: 0,
      };
    }
    
    if (candidateCount === 1) {
      return {
        status: 'SINGLETON',
        resolved: true,
        candidate: features[0],
        candidateCount: 1,
        confidence: calculateConfidence('SINGLETON'),
      };
    }
    
    // Múltiples candidatos → no es singleton
    return {
      status: 'NOT_RESOLVED',
      resolved: false,
      candidate: null,
      candidateCount,
      confidence: 0,
    };
  }
  
  /**
   * Estrategia FUZZY: mejor match por nombre entre candidatos
   */
  resolveFuzzy(
    nombre: string, 
    features: LocalFeature[], 
    config?: Partial<TSFConfig>
  ): TSFResult {
    const cfg = { ...this.config, ...config };
    const candidateCount = features.length;
    
    if (candidateCount === 0) {
      return {
        status: 'NO_LOCAL_DATA',
        resolved: false,
        candidate: null,
        candidateCount: 0,
        confidence: 0,
      };
    }
    
    // Demasiados candidatos → no intentar fuzzy
    if (candidateCount > cfg.maxFuzzyCandidates) {
      return {
        status: 'TOO_MANY_CANDIDATES',
        resolved: false,
        candidate: null,
        candidateCount,
        confidence: 0,
      };
    }
    
    // Usar FastFuzzy para encontrar mejor match
    const fuzzy = new FastFuzzy(features, { 
      keys: ['nombre', 'tipo', 'direccion', 'localidad'],
      threshold: 1 - cfg.minFuzzyScore, // FastFuzzy usa threshold inverso
    });
    
    const results = fuzzy.search(nombre, { limit: 1 });
    
    if (results.length > 0) {
      const best = results[0];
      // Convertir score de FastFuzzy (0=mejor, 1=peor) a nuestro formato (0-1 donde 1=mejor)
      const normalizedScore = 1 - best.score;
      
      if (normalizedScore >= cfg.minFuzzyScore) {
        return {
          status: 'FUZZY_BEST',
          resolved: true,
          candidate: best.item,
          candidateCount,
          fuzzyScore: normalizedScore,
          confidence: calculateConfidence('FUZZY_BEST', normalizedScore),
        };
      }
    }
    
    // Fallback: usar calculateSimilarity para match más fino
    let bestMatch: LocalFeature | null = null;
    let bestScore = 0;
    
    for (const feature of features) {
      const score = calculateSimilarity(nombre, feature.nombre) / 100;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = feature;
      }
    }
    
    if (bestMatch && bestScore >= cfg.minFuzzyScore) {
      return {
        status: 'FUZZY_BEST',
        resolved: true,
        candidate: bestMatch,
        candidateCount,
        fuzzyScore: bestScore,
        confidence: calculateConfidence('FUZZY_BEST', bestScore),
      };
    }
    
    return {
      status: 'NOT_RESOLVED',
      resolved: false,
      candidate: null,
      candidateCount,
      fuzzyScore: bestScore,
      confidence: 0,
    };
  }
  
  /**
   * Método principal: combina estrategias SINGLETON y FUZZY
   */
  async geocode(request: TSFGeocodingRequest): Promise<TSFGeocodingResponse> {
    const startTime = performance.now();
    const cfg = { ...this.config, ...request.config };
    
    // Fase 1: Buscar candidatos por municipio y tipo
    const features = await this.searchByMunicipio(
      request.codMunicipio, 
      request.tipo
    );
    
    let result: TSFResult;
    
    // Elegir estrategia
    if (request.estrategia === 'DISABLED') {
      result = {
        status: 'NOT_RESOLVED',
        resolved: false,
        candidate: null,
        candidateCount: features.length,
        confidence: 0,
      };
    } else if (features.length === 0) {
      result = {
        status: 'NO_LOCAL_DATA',
        resolved: false,
        candidate: null,
        candidateCount: 0,
        confidence: 0,
      };
    } else if (features.length === 1 && request.estrategia !== 'FUZZY') {
      // SINGLETON: único candidato
      result = this.resolveSingleton(features);
    } else {
      // FUZZY: múltiples candidatos o estrategia forzada
      result = this.resolveFuzzy(request.nombre, features, cfg);
    }
    
    // `PREFEDU-b2` DOP-1 (d): si los candidatos son solo centros educativos, el elegido (SINGLETON o FUZZY_BEST)
    // ha de pasar la guarda de `searchLocal`. Sin ella, SINGLETON daba el único colegio del municipio por bueno
    // para «Escuela de adultos» o «Guardería» (Benalúa de las Villas, `confirmed:document 100`).
    // `PREFEDU-b3` DOP-2 (a): un nombre que solo dice el tipo («Escuela Municipal de Música», «Colegio», «E.I.»,
    // «Guardería») vale con un único centro **de ese tipo** en el municipio, no con un único candidato, y se toma
    // ese centro (b2 perdía 13 «Escuela Municipal de Música»; con los sinónimos de b3, la regla anterior volvía a
    // dar el colegio de Benalúa por bueno).
    if (result.resolved && result.candidate && features.length > 0
      && features.every((f) => f.categoria === 'education')) {
      const unico = esSoloPrefijoEducativo(request.nombre)
        ? elegirUnicoDelTipo(request.nombre, features.map((f) => ({ f, nombre: nombreConTipoDera(f), tipoDera: f.tipoDera, x: f.x, y: f.y })))?.f ?? null
        : null;
      const elegido = unico ?? result.candidate;
      if (unico) result = { ...result, candidate: unico };
      const acepta = !!unico || !!elegirCentroEducativo(request.nombre, [elegido]);
      if (!acepta) {
        result = {
          status: 'NOT_RESOLVED',
          resolved: false,
          candidate: null,
          candidateCount: result.candidateCount,
          confidence: 0,
        };
      }
    }

    // Construir respuesta completa
    const tiempoMs = Math.round(performance.now() - startTime);
    
    const response: TSFGeocodingResponse = {
      ...result,
      tiempoMs,
      codMunicipioBuscado: request.codMunicipio,
    };
    
    // Añadir coordenadas si resuelto
    if (result.resolved && result.candidate) {
      response.coordenadas = {
        x: result.candidate.x,
        y: result.candidate.y,
      };
      response.nombreNormalizado = result.candidate.nombre;
      response.categoria = result.candidate.categoria;
    }
    
    return response;
  }
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default TSFGeocoder;
